import React, { useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import Button from "./button.js";

const DoubleCalendar = ({ onClose, onDateSelect, initialDateFrom, initialDateTo, chefAvailabilityExceptionDayDtoList, showQuickSelect = true }) => {
    // Initialize with the proper date range if provided
    const [range, setRange] = useState(() => {
        if (initialDateFrom && initialDateTo) {
            return [new Date(initialDateFrom), new Date(initialDateTo)];
        } else if (initialDateFrom) {
            return [new Date(initialDateFrom), null];
        }
        return [null, null];
    });
    const [firstMonth, setFirstMonth] = useState(() => {
        if (initialDateFrom) {
            return new Date(initialDateFrom);
        }
        return new Date();
    });
    const [secondMonth, setSecondMonth] = useState(() => {
        if (initialDateTo) {
            const date = new Date(initialDateTo);
            // If initialDateTo is in the same month as initialDateFrom, show next month
            if (initialDateFrom && 
                date.getMonth() === new Date(initialDateFrom).getMonth() && 
                date.getFullYear() === new Date(initialDateFrom).getFullYear()) {
                date.setMonth(date.getMonth() + 1);
            }
            return date;
        } else if (initialDateFrom) {
            const date = new Date(initialDateFrom);
            date.setMonth(date.getMonth() + 1);
            return date;
        }
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        return nextMonth;
    });
    const { language } = useLanguage();

    // Get locale for calendar based on language
    const getCalendarLocale = () => {
        const localeMap = {
            'am': 'hy-AM',
            'ru': 'ru-RU',
            'en': 'en-US'
        };
        return localeMap[language] || 'en-US';
    };

    // Format month and year with translation
    const formatMonthYear = (date) => {
        const monthNames = {
            'am': ['Հունվար', 'Փետրվար', 'Մարտ', 'Ապրիլ', 'Մայիս', 'Հունիս', 'Հուլիս', 'Օգոստոս', 'Սեպտեմբեր', 'Հոկտեմբեր', 'Նոյեմբեր', 'Դեկտեմբեր'],
            'ru': ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
            'en': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        };
        const month = monthNames[language]?.[date.getMonth()] || date.toLocaleString('default', { month: 'long' });
        return `${month} ${date.getFullYear()}`;
    };

    // Format weekday abbreviation
    const formatShortWeekday = (locale, date) => {
        const weekdayNames = {
            'am': ['Երկ', 'Երք', 'Չոր', 'Հնգ', 'Ուրբ', 'Շաբ', 'Կիր'],
            'ru': ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
            'en': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        };
        const dayIndex = date.getDay();
        return weekdayNames[language]?.[dayIndex] || date.toLocaleDateString(locale, { weekday: 'short' });
    };

    const handleDateChange = (date) => {
        // Normalize the clicked date to avoid timezone issues
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        
        // Normalize existing range dates for comparison
        const normalizeDateForComparison = (d) => {
            if (!d) return null;
            const normalized = new Date(d);
            normalized.setHours(0, 0, 0, 0);
            return normalized.getTime();
        };
        
        const currentStart = range[0] ? normalizeDateForComparison(range[0]) : null;
        const currentEnd = range[1] ? normalizeDateForComparison(range[1]) : null;
        const clickedTime = normalizeDateForComparison(normalizedDate);
        
        if (!currentStart || (currentStart && currentEnd)) {
            // First click or starting a new range after completing one
            // Clear any existing range and start fresh
            setRange([normalizedDate, null]);
        } else if (currentStart && !currentEnd) {
            // Second click to complete the range
            // If clicking the same date as start, clear the selection
            if (clickedTime === currentStart) {
                setRange([null, null]);
            } else {
                // If second click is before first, swap them
                if (clickedTime < currentStart) {
                    setRange([normalizedDate, new Date(range[0])]);
                } else {
                    setRange([new Date(range[0]), normalizedDate]);
                }
            }
        }
    };

    const isDateInRange = (date) => {
        const [start, end] = range;
        if (!start || !end) return false;

        // Normalize dates to compare only date part (ignore time)
        const normalizeDate = (d) => {
            if (!d) return null;
            const normalized = new Date(d);
            normalized.setHours(0, 0, 0, 0);
            return normalized.getTime();
        };

        const dateTime = normalizeDate(date);
        const startTime = normalizeDate(start);
        const endTime = normalizeDate(end);

        // Only return true if we have valid times and date is strictly between start and end
        // (exclusive of start and end since they have their own styling)
        if (!dateTime || !startTime || !endTime) return false;

        return dateTime > startTime && dateTime < endTime;
    };

    const isStartOrEndDate = (date) => {
        const [start, end] = range;
        if (!start) return false;
        
        // Normalize dates to compare only date part (ignore time)
        const normalizeDate = (d) => {
            if (!d) return null;
            const normalized = new Date(d);
            normalized.setHours(0, 0, 0, 0);
            return normalized.getTime();
        };
        
        const dateTime = normalizeDate(date);
        const startTime = normalizeDate(start);
        const endTime = normalizeDate(end);
        
        // Only return true if date exactly matches start OR end (not both, and not if end is null)
        if (dateTime === startTime) {
            return true;
        }
        if (endTime && dateTime === endTime) {
            return true;
        }
        return false;
    };

    const isDateDisabled = (date) => {
        const formattedDate = date.toLocaleDateString("fr-CA");
        return chefAvailabilityExceptionDayDtoList?.some(item => item.exceptionDate === formattedDate);
    };


    const handleConfirm = () => {
        if (range[0]) {
            if (!range[1]) {
                // Single day selection
                const selectedDate = new Date(range[0]);
                // Use toLocaleDateString to avoid timezone issues
                const formattedDate = selectedDate.toLocaleDateString('fr-CA'); // formats as YYYY-MM-DD
                onDateSelect([formattedDate]);
            } else {
                // Date range selection
                const startDate = new Date(range[0]);
                const endDate = new Date(range[1]);

                const dateList = [];
                const currentDate = new Date(startDate);

                // Include all dates from start to end (inclusive)
                while (currentDate <= endDate) {
                    dateList.push(new Date(currentDate));
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                // Use toLocaleDateString to avoid timezone issues
                const formattedDates = dateList.map(date => date.toLocaleDateString('fr-CA')); // formats as YYYY-MM-DD
                onDateSelect(formattedDates);
            }
            onClose();
        }
    };

    return (
        <div className="space-y-4">
            {/* Quick presets - Conditionally visible */}
            {showQuickSelect && (
                <div className="space-y-3 p-3 sm:p-4 bg-white dark:bg-[#0d1117] rounded-lg border dark:border-[#30363d]">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-[#c9d1d9]">{getTranslation('common.quickSelect', language)}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <button
                            className="w-full text-xs px-3 py-2 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#1c2128] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors font-medium text-gray-700 dark:text-[#c9d1d9] touch-manipulation"
                            onClick={() => {
                                const today = new Date();
                                const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                                setRange([thisMonthStart, today]);
                                // Navigate calendars to show the selected period
                                setFirstMonth(new Date(thisMonthStart));
                                setSecondMonth(new Date(today));
                            }}
                        >
                            {getTranslation('common.thisMonth', language)}
                        </button>
                        <button
                            className="w-full text-xs px-3 py-2 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#1c2128] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors font-medium text-gray-700 dark:text-[#c9d1d9] touch-manipulation"
                            onClick={() => {
                                const today = new Date();
                                const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                                setRange([lastMonthStart, lastMonthEnd]);
                                // Navigate calendars to show the selected period
                                setFirstMonth(new Date(lastMonthStart));
                                setSecondMonth(new Date(lastMonthEnd));
                            }}
                        >
                            {getTranslation('common.lastMonth', language)}
                        </button>
                        <button
                            className="w-full text-xs px-3 py-2 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#1c2128] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors font-medium text-gray-700 dark:text-[#c9d1d9] touch-manipulation sm:col-span-1"
                            onClick={() => {
                                const today = new Date();
                                const sevenDaysAgo = new Date(today);
                                sevenDaysAgo.setDate(today.getDate() - 7);
                                setRange([sevenDaysAgo, today]);
                                // Navigate calendars to show the selected period
                                setFirstMonth(new Date(sevenDaysAgo));
                                // If the range spans two months, show both months; otherwise show current month and next
                                if (sevenDaysAgo.getMonth() !== today.getMonth() || sevenDaysAgo.getFullYear() !== today.getFullYear()) {
                                    setSecondMonth(new Date(today));
                                } else {
                                    const nextMonth = new Date(today);
                                    nextMonth.setMonth(nextMonth.getMonth() + 1);
                                    setSecondMonth(nextMonth);
                                }
                            }}
                        >
                            {getTranslation('common.last7Days', language)}
                        </button>
                    </div>
                </div>
            )}

            {/* Calendar section */}
            <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-6">
                {/* First Calendar */}
                <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg p-3 sm:p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <button 
                            className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors text-gray-600 dark:text-[#8b949e] touch-manipulation"
                            onClick={() => setFirstMonth(new Date(firstMonth.setMonth(firstMonth.getMonth() - 1)))}
                        >
                            <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-[#c9d1d9] text-center px-2">
                            {formatMonthYear(firstMonth)}
                        </h3>
                        <button 
                            className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors text-gray-600 dark:text-[#8b949e] touch-manipulation"
                            onClick={() => setFirstMonth(new Date(firstMonth.setMonth(firstMonth.getMonth() + 1)))}
                        >
                            <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <Calendar
                            activeStartDate={firstMonth}
                            onClickDay={handleDateChange}
                            tileDisabled={({ date }) => isDateDisabled(date)}
                            tileClassName={({ date, view }) => {
                                // Only apply classes in month view
                                if (view !== 'month') return "";
                                
                                if (isDateDisabled(date)) {
                                    return "disabled-date";
                                }
                                
                                // Check if this is a start or end date (highest priority)
                                if (isStartOrEndDate(date)) {
                                    return "selected-date";
                                }
                                
                                // Check if this date is in the range (between start and end)
                                if (isDateInRange(date)) {
                                    return "in-range";
                                }
                                
                                // Return empty string - don't apply any selection classes
                                // The current date will use react-calendar's default --now styling
                                return "";
                            }}
                            selectRange={false}
                            showNavigation={false}
                            className="mobile-calendar"
                            locale={getCalendarLocale()}
                            formatShortWeekday={(locale, date) => formatShortWeekday(locale, date)}
                            value={null}
                        />
                    </div>
                </div>

                {/* Second Calendar */}
                <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg p-3 sm:p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <button 
                            className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors text-gray-600 dark:text-[#8b949e] touch-manipulation"
                            onClick={() => setSecondMonth(new Date(secondMonth.setMonth(secondMonth.getMonth() - 1)))}
                        >
                            <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-[#c9d1d9] text-center px-2">
                            {formatMonthYear(secondMonth)}
                        </h3>
                        <button 
                            className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] hover:border-gray-400 dark:hover:border-[#484f58] rounded-md transition-colors text-gray-600 dark:text-[#8b949e] touch-manipulation"
                            onClick={() => setSecondMonth(new Date(secondMonth.setMonth(secondMonth.getMonth() + 1)))}
                        >
                            <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <Calendar
                            activeStartDate={secondMonth}
                            onClickDay={handleDateChange}
                            tileDisabled={({ date }) => isDateDisabled(date)}
                            tileClassName={({ date, view }) => {
                                // Only apply classes in month view
                                if (view !== 'month') return "";
                                
                                if (isDateDisabled(date)) {
                                    return "disabled-date";
                                }
                                
                                // Check if this is a start or end date (highest priority)
                                if (isStartOrEndDate(date)) {
                                    return "selected-date";
                                }
                                
                                // Check if this date is in the range (between start and end)
                                if (isDateInRange(date)) {
                                    return "in-range";
                                }
                                
                                // Return empty string - don't apply any selection classes
                                // The current date will use react-calendar's default --now styling
                                return "";
                            }}
                            selectRange={false}
                            showNavigation={false}
                            className="mobile-calendar"
                            locale={getCalendarLocale()}
                            formatShortWeekday={(locale, date) => formatShortWeekday(locale, date)}
                            value={null}
                        />
                    </div>
                </div>
            </div>
                    
            {/* Action buttons */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-2 space-y-reverse sm:space-y-0 pt-4 border-t border-gray-200 dark:border-[#30363d]">
                <Button
                    variant="secondary"
                    size="md"
                    onClick={onClose}
                    className="w-full sm:w-auto"
                >
                    {getTranslation('common.cancel', language)}
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleConfirm}
                    disabled={!range[0]}
                    className="w-full sm:w-auto"
                >
                    {getTranslation('common.confirm', language)}
                </Button>
            </div>
        </div>
    );
};
export default DoubleCalendar;
